import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { motion } from "framer-motion";

/**
 * Three phone mockups side-by-side with hover lift and click-to-zoom lightbox.
 * steps: [{ img, alt, title, desc }]
 */
export default function PhoneShowcase({ steps }) {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);

    const openAt = (i) => {
        setActive(i);
        setOpen(true);
    };

    return (
        <>
            <div
                className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10"
                data-testid="phone-showcase"
            >
                {steps.map((s, i) => (
                    <motion.button
                        key={s.title}
                        type="button"
                        onClick={() => openAt(i)}
                        data-testid={`phone-step-${i}`}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                            duration: 0.7,
                            ease: [0.16, 1, 0.3, 1],
                            delay: i * 0.12,
                        }}
                        className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981] focus-visible:ring-offset-4 focus-visible:ring-offset-transparent rounded-3xl"
                        aria-label={`View ${s.title}`}
                    >
                        <div className="relative flex items-center justify-center h-[520px] lg:h-[560px] transition-transform duration-500 group-hover:-translate-y-2">
                            {/* soft green glow on hover */}
                            <div className="absolute inset-x-6 bottom-2 h-32 rounded-full bg-[#10B981]/0 group-hover:bg-[#10B981]/25 blur-3xl transition-colors duration-500" />
                            <img
                                src={s.img}
                                alt={s.alt}
                                className="relative z-10 max-h-full w-auto object-contain rounded-[36px] mx-phone-shadow transition-shadow duration-500 group-hover:drop-shadow-[0_40px_60px_rgba(16,185,129,0.25)]"
                                draggable={false}
                            />
                            {/* zoom hint */}
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 translate-y-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 z-20">
                                <span className="inline-flex items-center gap-1 rounded-full bg-[#070F1D] text-white text-[11px] font-bold tracking-wider uppercase px-3 py-1.5 shadow-lg">
                                    Tap to zoom
                                </span>
                            </div>
                        </div>
                        <div className="mt-8 text-center">
                            <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-[#070F1D] group-hover:text-[#10B981] transition-colors">
                                {s.title}
                            </h3>
                            <p className="mt-3 text-slate-600 leading-relaxed max-w-xs mx-auto">
                                {s.desc}
                            </p>
                        </div>
                    </motion.button>
                ))}
            </div>

            {/* Lightbox */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    data-testid="phone-lightbox"
                    className="max-w-[96vw] sm:max-w-xl md:max-w-2xl bg-transparent border-0 p-0 shadow-none [&>button]:text-white [&>button]:bg-black/40 [&>button]:rounded-full [&>button]:p-2 [&>button]:backdrop-blur-md [&>button]:border-0 [&>button]:hover:bg-black/60"
                >
                    <DialogTitle className="sr-only">
                        {steps[active]?.title}
                    </DialogTitle>
                    <div className="relative flex items-center justify-center">
                        <div className="absolute inset-x-12 bottom-0 h-48 bg-[#10B981]/40 blur-3xl rounded-full pointer-events-none" />
                        <img
                            src={steps[active]?.img}
                            alt={steps[active]?.alt}
                            className="relative z-10 max-h-[86vh] w-auto object-contain rounded-[40px] drop-shadow-[0_40px_80px_rgba(0,0,0,0.5)]"
                        />
                    </div>
                    <div className="relative mt-4 text-center">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#34D399] font-bold">
                            Step {active + 1} of {steps.length}
                        </div>
                        <h3 className="mt-1 text-white font-black text-2xl">
                            {steps[active]?.title}
                        </h3>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
